<script lang="ts">
  import { getContext, type Snippet } from 'svelte';

  import { ContextKeyLabel } from '$provider/constants/ContextKeyLabel';
  import EntityView from '$shared/organisms/EntityView/EntityView.svelte';
  import type { EntityViewProps, EntityViewType } from '$shared/components/types';
  import { NewEntityView } from '$shared/components';

  interface Props {
    options?: EntityViewProps;
    newOptions?: EntityViewType;
    resultMeta?: Snippet;
    actions?: Snippet;
    header?: Snippet;
    meta?: Snippet;
    result?: Snippet;
    screenshot?: Snippet;
    onalert?: (alert: any) => void;
    onedit?: () => void;
    onevent?: (payload: any) => void;
    onoption?: (option: any) => void;
    onremove?: () => void;
    onreverify?: () => void;
    ontab?: (tab: any) => void;
    onclickalert?: (alert: any) => void;
    onclickoption?: (option: any) => void;
    ontabchanged?: (tab: any) => void;
  }

  let {
    options = {},
    newOptions = {} as EntityViewType,
    resultMeta = undefined,
    actions = undefined,
    header = undefined,
    meta = undefined,
    result = undefined,
    screenshot = undefined,
    onalert,
    onedit,
    onevent,
    onoption,
    onremove,
    onreverify,
    ontab,
    onclickalert,
    onclickoption,
    ontabchanged
  }: Props = $props();

  const context = getContext<ReadonlyContextType>(ContextKeyLabel.Readonly);

  const UserActionOptions = ['merge', 'remove'];

  export const refreshTab = async (): Promise<void> => {
    await entityView?.refreshTab();
  };
  export const setTab = (tab: string): void => {
    newEntityView?.setTab(tab);
  };

  let entityView: EntityView = $state()!;
  let newEntityView: NewEntityView = $state()!;

  const isNew = $derived(Object.values(newOptions || {}).length !== 0);

  // Derive options with user action options additionally disabled when in readonly context.
  // Uses $derived (not $effect) so child components re-render reactively; avoids overwriting
  // the store's own disabled state (e.g. admin checks) with `||` instead of direct assignment.
  const effectiveOptions = $derived.by(() => ({
    ...options,
    options: options?.options?.map((opt) => {
      if (!opt.id || !UserActionOptions.includes(opt.id)) return opt;
      return { ...opt, disabled: opt.disabled || !!$context?.readonly };
    })
  }));

  const effectiveNewOptions = $derived.by(() => ({
    ...newOptions,
    header: {
      ...newOptions?.header,
      moreOption: newOptions?.header?.moreOption
        ? {
            ...newOptions.header.moreOption,
            list: newOptions.header.moreOption.list?.map((opt) => {
              if (!opt.id || !UserActionOptions.includes(opt.id)) return opt;
              return { ...opt, disabled: opt.disabled || !!$context?.readonly };
            })
          }
        : newOptions?.header?.moreOption
    }
  }));
</script>

{#snippet profileResult()}
  <div class="profile-result">
    {@render result?.()}
  </div>
{/snippet}

{#if isNew}
  <NewEntityView
    bind:this={newEntityView}
    options={effectiveNewOptions as EntityViewType}
    readonly={$context?.readonly}
    {resultMeta}
    {actions}
    {onalert}
    {onedit}
    {onevent}
    onoption={onoption}
    {onremove}
    {onreverify}
    {ontab}
  />
{:else}
  <EntityView
    bind:this={entityView}
    {...effectiveOptions}
    readonly={$context?.readonly}
    {resultMeta}
    customActions={actions}
    {header}
    {meta}
    result={profileResult}
    {screenshot}
    onclickalert={onclickalert}
    onclickoption={onclickoption}
    {onedit}
    {onremove}
    {onreverify}
    ontabchanged={ontabchanged}
    {onevent}
  />
{/if}

<style>
  .profile-result {
    width: inherit;
  }
</style>
